// Copy text to the clipboard with a fallback for insecure contexts
// (e.g. serving the app over plain HTTP on a LAN IP like
// http://192.168.x.x:3000). The modern `navigator.clipboard.writeText`
// API is only available in a secure context (HTTPS / localhost); when
// it's unavailable we fall back to the legacy `document.execCommand('copy')`
// technique using a hidden textarea.
//
// Returns a Promise that resolves to `true` on success and `false` on failure.
export function copyToClipboard(text) {
  const value = text == null ? '' : String(text);

  // Modern path: only works in secure contexts.
  if (
    typeof navigator !== 'undefined' &&
    navigator.clipboard &&
    typeof navigator.clipboard.writeText === 'function' &&
    typeof window !== 'undefined' &&
    window.isSecureContext
  ) {
    return navigator.clipboard
      .writeText(value)
      .then(() => true)
      .catch(() => fallbackCopy(value));
  }

  // Insecure context (HTTP over LAN IP, etc.) – use legacy fallback.
  return Promise.resolve(fallbackCopy(value));
}

function fallbackCopy(text) {
  if (typeof document === 'undefined') return false;

  const textarea = document.createElement('textarea');
  textarea.value = text;
  // Prevent scroll jumps and keep the element out of sight.
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '0';
  textarea.style.left = '0';
  textarea.style.width = '1px';
  textarea.style.height = '1px';
  textarea.style.padding = '0';
  textarea.style.border = 'none';
  textarea.style.outline = 'none';
  textarea.style.boxShadow = 'none';
  textarea.style.background = 'transparent';
  textarea.style.opacity = '0';

  document.body.appendChild(textarea);

  // Preserve the caller's current selection if any.
  const selection = document.getSelection();
  const previousRange =
    selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

  let ok = false;
  try {
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);
    ok = document.execCommand('copy');
  } catch (err) {
    ok = false;
  } finally {
    document.body.removeChild(textarea);

    if (previousRange && selection) {
      selection.removeAllRanges();
      selection.addRange(previousRange);
    }
  }

  return ok;
}

export default copyToClipboard;
